import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown,
  BarChart3, 
  PieChart,
  Calendar,
  Target,
  Zap,
  Clock,
  DollarSign,
  Users,
  RefreshCw,
  Filter
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';
import ApiService from '../services/api';

interface AnalyticsData {
  processing_trends: Array<{
    date: string;
    offers_processed: number;
    products_extracted: number;
    success_rate: number;
    avg_processing_time: number;
  }>;
  performance_metrics: {
    avg_confidence: number;
    total_processing_time: number;
    accuracy_trend: number; // percentage change
    speed_improvement: number; // percentage change
  };
  usage_patterns: {
    peak_hours: Array<{ hour: number; activity: number }>;
    busiest_day: string;
    user_activity: Array<{ date: string; active_users: number }>;
  };
  cost_analysis: {
    gemini_api_calls: number;
    processing_cost: number;
    cost_per_product: number;
    monthly_trend: number;
  };
  merchant_insights: Array<{
    name: string;
    offers_count: number;
    products_count: number;
    avg_discount: number;
    success_rate: number;
  }>;
  category_performance: Array<{
    name: string;
    count: number;
    avg_confidence: number;
    color: string;
  }>;
}

const AdvancedAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [refreshing, setRefreshing] = useState(false);

  const generateMockAnalyticsData = (): AnalyticsData => {
    // Generate processing trends for the last 30 days
    const processingTrends = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      return {
        date: date.toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' }),
        offers_processed: Math.floor(Math.random() * 10) + 2,
        products_extracted: Math.floor(Math.random() * 50) + 20,
        success_rate: Math.random() * 20 + 80, // 80-100%
        avg_processing_time: Math.random() * 30 + 15 // 15-45 seconds
      };
    });

    // Generate peak hours data
    const peakHours = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      activity: hour >= 9 && hour <= 17 
        ? Math.floor(Math.random() * 50) + 30 // Higher activity during business hours
        : Math.floor(Math.random() * 20) + 5  // Lower activity at night
    }));

    // Generate user activity data
    const userActivity = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return {
        date: date.toLocaleDateString('ar-SA', { weekday: 'short' }),
        active_users: Math.floor(Math.random() * 20) + 5
      };
    });

    // Generate merchant insights
    const merchants = [
      'كارفور', 'لولو هايبرماركت', 'العثيم', 'الدانوب', 'بنده', 'هايبر بندة'
    ];
    const merchantInsights = merchants.map(name => ({
      name,
      offers_count: Math.floor(Math.random() * 15) + 5,
      products_count: Math.floor(Math.random() * 200) + 50,
      avg_discount: Math.random() * 30 + 10,
      success_rate: Math.random() * 15 + 85
    }));

    // Generate category performance
    const categories = ['مواد غذائية', 'ألبان', 'خضروات', 'مشروبات', 'معلبات', 'مخابز'];
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    const categoryPerformance = categories.map((name, index) => ({
      name,
      count: Math.floor(Math.random() * 100) + 20,
      avg_confidence: Math.random() * 20 + 80,
      color: colors[index]
    }));

    return {
      processing_trends: processingTrends,
      performance_metrics: {
        avg_confidence: Math.random() * 10 + 90,
        total_processing_time: Math.random() * 1000 + 500,
        accuracy_trend: Math.random() * 10 + 2, // 2-12% improvement
        speed_improvement: Math.random() * 15 + 5 // 5-20% improvement
      },
      usage_patterns: {
        peak_hours: peakHours,
        busiest_day: 'الثلاثاء',
        user_activity: userActivity
      },
      cost_analysis: {
        gemini_api_calls: Math.floor(Math.random() * 5000) + 2000,
        processing_cost: Math.random() * 200 + 100,
        cost_per_product: Math.random() * 0.5 + 0.2,
        monthly_trend: Math.random() * 20 - 10 // -10% to +10%
      },
      merchant_insights: merchantInsights,
      category_performance: categoryPerformance
    };
  };

  const loadAnalyticsData = async () => {
    try {
      setRefreshing(true);
      setLoading(true);
      
      // In a real implementation, this would call specific analytics endpoints
      // For now, we'll use mock data combined with some real API calls
      await Promise.all([
        ApiService.getAllOffers(),
        ApiService.getProducts({ limit: 100 }),
        ApiService.getMerchants()
      ]);
      
      const mockData = generateMockAnalyticsData();
      setAnalyticsData(mockData);
      
    } catch (error) {
      console.error('Failed to load analytics data:', error);
      // Still show mock data even if API fails
      setAnalyticsData(generateMockAnalyticsData());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('ar-SA').format(Math.round(num));
  };

  const getTrendIcon = (trend: number) => {
    return trend > 0 ? 
      <TrendingUp className="w-4 h-4 text-success-600" /> : 
      <TrendingDown className="w-4 h-4 text-error-600" />;
  };

  const getTrendColor = (trend: number) => {
    return trend > 0 ? 'text-success-600' : 'text-error-600';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card-gradient">
          <div className="card-header">
            <h3 className="font-bold text-gray-900">التحليلات المتقدمة</h3>
          </div>
          <div className="card-body">
            <div className="animate-pulse space-y-4">
              <div className="grid grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) return null;

  return (
    <div className="space-y-6">
      {/* Advanced Metrics Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-gradient"
      >
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              التحليلات المتقدمة
            </h3>
            <div className="flex items-center gap-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
                className="input text-sm"
              >
                <option value="7d">آخر 7 أيام</option>
                <option value="30d">آخر 30 يوم</option>
                <option value="90d">آخر 90 يوم</option>
              </select>
              <button
                onClick={loadAnalyticsData}
                disabled={refreshing}
                className="btn-outline text-sm"
              >
                <RefreshCw className={`w-4 h-4 ml-1 ${refreshing ? 'animate-spin' : ''}`} />
                تحديث
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          {/* Performance Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="stat-card-mini">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5 text-primary-600" />
                <div className="flex items-center gap-1">
                  {getTrendIcon(analyticsData.performance_metrics.accuracy_trend)}
                  <span className={`text-xs font-medium ${getTrendColor(analyticsData.performance_metrics.accuracy_trend)}`}>
                    +{analyticsData.performance_metrics.accuracy_trend.toFixed(1)}%
                  </span>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">
                {Math.round(analyticsData.performance_metrics.avg_confidence)}%
              </h4>
              <p className="text-sm text-gray-600">متوسط دقة الاستخراج</p>
            </div>

            <div className="stat-card-mini">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-5 h-5 text-secondary-600" />
                <div className="flex items-center gap-1">
                  {getTrendIcon(analyticsData.performance_metrics.speed_improvement)}
                  <span className={`text-xs font-medium ${getTrendColor(analyticsData.performance_metrics.speed_improvement)}`}>
                    +{analyticsData.performance_metrics.speed_improvement.toFixed(1)}%
                  </span>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">
                {formatNumber(analyticsData.processing_trends.reduce((sum, d) => sum + d.avg_processing_time, 0) / analyticsData.processing_trends.length)}s
              </h4>
              <p className="text-sm text-gray-600">متوسط وقت المعالجة</p>
            </div>

            <div className="stat-card-mini">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-indigo-600" />
                <div className="flex items-center gap-1">
                  {getTrendIcon(analyticsData.cost_analysis.monthly_trend)}
                  <span className={`text-xs font-medium ${getTrendColor(analyticsData.cost_analysis.monthly_trend)}`}>
                    {analyticsData.cost_analysis.monthly_trend > 0 ? '+' : ''}{analyticsData.cost_analysis.monthly_trend.toFixed(1)}%
                  </span>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">
                {analyticsData.cost_analysis.cost_per_product.toFixed(2)} ر.س
              </h4>
              <p className="text-sm text-gray-600">تكلفة لكل منتج</p>
            </div>

            <div className="stat-card-mini">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-purple-600" />
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-gray-500" />
                  <span className="text-xs text-gray-500">يوم {analyticsData.usage_patterns.busiest_day}</span>
                </div>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">
                {formatNumber(analyticsData.usage_patterns.user_activity.reduce((sum, d) => sum + d.active_users, 0))}
              </h4>
              <p className="text-sm text-gray-600">نشاط المستخدمين</p>
            </div>
          </div>

          {/* Processing Trends Chart */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary-600" />
              اتجاهات المعالجة ({timeRange === '7d' ? 'أسبوعي' : timeRange === '30d' ? 'شهري' : 'ربع سنوي'})
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analyticsData.processing_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="offers_processed"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                    name="العروض المعالجة"
                  />
                  <Area
                    type="monotone"
                    dataKey="products_extracted"
                    stackId="2"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                    name="المنتجات المستخرجة"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Detailed Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Hours Heatmap */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="card-gradient"
        >
          <div className="card-header">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-600" />
              ساعات الذروة
            </h4>
          </div>
          <div className="card-body">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analyticsData.usage_patterns.peak_hours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(hour) => `${hour}:00`}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number) => [value, 'النشاط']}
                    labelFormatter={(hour) => `الساعة ${hour}:00`}
                  />
                  <Bar 
                    dataKey="activity" 
                    fill="#8b5cf6"
                    name="مستوى النشاط"
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Category Performance */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="card-gradient"
        >
          <div className="card-header">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <PieChart className="w-4 h-4 text-secondary-600" />
              أداء الفئات
            </h4>
          </div>
          <div className="card-body">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analyticsData.category_performance}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                  >
                    {analyticsData.category_performance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Merchant Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-gradient"
      >
        <div className="card-header">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-600" />
            تقرير أداء المتاجر
          </h4>
        </div>
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="table-header">المتجر</th>
                  <th className="table-header">العروض</th>
                  <th className="table-header">المنتجات</th>
                  <th className="table-header">متوسط الخصم</th>
                  <th className="table-header">معدل النجاح</th>
                  <th className="table-header">الأداء</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.merchant_insights.slice(0, 5).map((merchant, index) => (
                  <tr key={merchant.name} className="hover:bg-gray-50/50">
                    <td className="table-cell">
                      <div className="font-medium text-gray-900">{merchant.name}</div>
                    </td>
                    <td className="table-cell">{merchant.offers_count}</td>
                    <td className="table-cell">{merchant.products_count}</td>
                    <td className="table-cell">
                      <span className="text-success-600 font-medium">
                        {merchant.avg_discount.toFixed(0)}%
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className="text-primary-600 font-medium">
                        {merchant.success_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 ml-2">
                          <div 
                            className="h-2 bg-primary-500 rounded-full transition-all duration-300"
                            style={{ width: `${merchant.success_rate}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">
                          {merchant.success_rate > 95 ? 'ممتاز' : 
                           merchant.success_rate > 90 ? 'جيد جداً' :
                           merchant.success_rate > 85 ? 'جيد' : 'يحتاج تحسين'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdvancedAnalytics;